/**
 * Does the reviewer have work in progress that a reload would destroy?
 *
 * **Why a registry rather than a look at the page.** The first version of this asked the DOM: sweep
 * every mounted `textarea` and `input` and call any non-empty one unsaved. Cross-model review took
 * it apart, and it was right on both sides of the error:
 *
 * - It **misses** work the DOM cannot show. `ratingNote` lives in state and survives its panel being
 *   closed (`MicroLessonReview.tsx`), and `draftPatch` — the actual content edit, the most
 *   expensive thing on the screen — is never a text field at all.
 * - It **invents** work that does not exist, because a field populated from the server looks exactly
 *   like a field somebody typed into.
 *
 * So each screen declares its own dirtiness, in the terms it already computes for its own "Unsaved"
 * badge and its own approve button. The DOM sweep is kept underneath as a second, independent
 * signal — a screen that forgets to register is then still partially covered — and the two are OR'd,
 * because the safe answer is always "yes, there is work".
 *
 * The probe holds a **ref**, not a value: a registration captured at mount would answer with the
 * dirtiness the screen had when it opened, which is `false` for every screen that has ever been
 * opened.
 */

import { useEffect, useRef } from 'react';

type DirtyProbe = () => boolean;

const probes = new Set<DirtyProbe>();

/** Registers a probe. Returns the deregistration function; call it on unmount. */
export function registerUnsavedWorkProbe(probe: DirtyProbe): () => void {
  probes.add(probe);
  return () => {
    probes.delete(probe);
  };
}

/**
 * True if any registered screen reports work in progress.
 *
 * A probe that throws is treated as dirty. It cannot answer, and the only safe reading of "I do not
 * know whether this reviewer has unsaved work" is that they do.
 */
export function hasRegisteredUnsavedWork(): boolean {
  for (const probe of probes) {
    try {
      if (probe()) {
        return true;
      }
    } catch {
      return true;
    }
  }
  return false;
}

/** Test seam — the registry is module state and would otherwise leak between tests. */
export function resetUnsavedWorkProbesForTest(): void {
  probes.clear();
}

/**
 * Declares this component's unsaved-work state for as long as it is mounted.
 *
 * Deliberately takes a boolean the component already computes, rather than a callback: every screen
 * that needs this already derives one for its own UI, and asking for a second, separately-maintained
 * definition is how the two drift apart.
 */
export function useUnsavedWorkProbe(isDirty: boolean): void {
  const latest = useRef(isDirty);
  latest.current = isDirty;
  useEffect(() => registerUnsavedWorkProbe(() => latest.current), []);
}
